// src/components/BookLibrary.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const BookLibrary = ({ auth, onBack }) => {
  const [library, setLibrary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBook, setSelectedBook] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isReading, setIsReading] = useState(false);
  const [readMode, setReadMode] = useState('online');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [showDownloadOptions, setShowDownloadOptions] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://shop.neoteeforge.com/neotee-backend';

  useEffect(() => {
    if (auth) {
      loadLibrary();
    }
  }, [auth]);

  const loadLibrary = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/books/library`, {
        headers: {
          'Authorization': `Bearer ${auth?.token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const libraryData = data.library || data.books || [];
        setLibrary(libraryData);
      } else {
        console.error('Failed to load library');
      }
    } catch (error) {
      console.error('Error loading library:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort library
  const getFilteredLibrary = () => {
    let filtered = [...library];
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(book => 
        book.title?.toLowerCase().includes(query) ||
        book.author?.toLowerCase().includes(query) ||
        book.description?.toLowerCase().includes(query)
      );
    }
    
    // Apply sorting
    switch (sortBy) {
      case 'recent':
        filtered.sort((a, b) => new Date(b.last_read || b.purchased_at) - new Date(a.last_read || a.purchased_at));
        break;
      case 'title':
        filtered.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
        break;
      case 'author':
        filtered.sort((a, b) => (a.author || '').localeCompare(b.author || ''));
        break;
      case 'downloads':
        filtered.sort((a, b) => (b.download_count || 0) - (a.download_count || 0));
        break;
      default:
        filtered.sort((a, b) => new Date(b.purchased_at) - new Date(a.purchased_at));
    }
    
    return filtered;
  };

  const handleReadBook = async (bookId) => {
    try {
      const book = library.find(b => b.id === bookId);
      if (!book) return;
      
      setSelectedBook(book);
      setIsReading(true);
      
      // Get the PDF URL
      const response = await fetch(`${API_BASE_URL}/api/books/${bookId}/read`, {
        headers: {
          'Authorization': `Bearer ${auth?.token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.pdf_url) {
          setPdfUrl(data.pdf_url);
          setTotalPages(data.total_pages || book.pdf_pages || 1);
          setCurrentPage(1);
          setReadMode('online');
          
          // Update last read time
          await fetch(`${API_BASE_URL}/api/books/${bookId}/update-last-read`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${auth?.token}`,
              'Content-Type': 'application/json'
            }
          });
        }
      } else {
        throw new Error('Failed to load book');
      }
    } catch (error) {
      console.error('Error reading book:', error);
      alert('Failed to load the book. Please try again.');
      setIsReading(false);
    }
  };

  const handleDownloadBook = async (bookId, format = 'pdf') => {
    try {
      const book = library.find(b => b.id === bookId);
      if (!book) return;
      
      setShowDownloadOptions(null);
      
      let endpoint = `${API_BASE_URL}/api/books/${bookId}/download`;
      let filename = `${book.title.replace(/[^a-z0-9]/gi, '_')}.pdf`;
      
      if (format === 'epub') {
        endpoint = `${API_BASE_URL}/api/books/${bookId}/download-epub`;
        filename = `${book.title.replace(/[^a-z0-9]/gi, '_')}.epub`;
      } else if (format === 'sample') {
        endpoint = `${API_BASE_URL}/api/books/${bookId}/download-sample`;
        filename = `${book.title.replace(/[^a-z0-9]/gi, '_')}_sample.pdf`;
      }
      
      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${auth?.token}`
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        // Update download count
        await fetch(`${API_BASE_URL}/api/books/${bookId}/update-download-count`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${auth?.token}`,
            'Content-Type': 'application/json'
          }
        });
        
        // Refresh library to show updated counts
        loadLibrary();
        
      } else {
        throw new Error(`Download failed: HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('Error downloading book:', error);
      alert('Failed to download the book. Please try again.');
    }
  };

  const handleBulkDownload = async () => {
    if (window.confirm(`Download all ${library.length} books as a ZIP file?`)) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/books/bulk-download`, {
          headers: {
            'Authorization': `Bearer ${auth?.token}`
          }
        });
        
        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `My_Book_Library_${new Date().toISOString().slice(0, 10)}.zip`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        } else {
          throw new Error('Bulk download failed');
        }
      } catch (error) {
        console.error('Error downloading bulk:', error);
        alert('Failed to download bulk collection. Please try downloading individual books.');
      }
    }
  };

  const handleCloseReader = () => {
    setIsReading(false);
    setSelectedBook(null);
    setPdfUrl(null);
    setCurrentPage(1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    
    return date.toLocaleDateString('en-IN', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return bytes + ' bytes';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (!auth) {
    return (
      <div className="library-container">
        <style jsx>{`
          .library-container {
            min-height: 100vh;
            background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 1rem;
            margin-top: 80px;
          }

          .auth-required {
            width: 100%;
            max-width: 500px;
            text-align: center;
            padding: 3rem 2rem;
            background: white;
            border-radius: 16px;
            box-shadow: 0 4px 20px rgba(0,0,0,0.08);
          }

          .auth-title {
            font-family: 'Playfair Display', serif;
            color: #334155;
            margin-bottom: 1rem;
            font-size: 1.8rem;
          }

          .auth-message {
            color: #64748b;
            margin-bottom: 2rem;
            font-size: 1rem;
            line-height: 1.6;
          }

          .back-button {
            padding: 0.875rem 1.75rem;
            background: #e63946;
            color: white;
            border: none;
            border-radius: 8px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
            font-size: 0.95rem;
          }

          .back-button:hover {
            background: #d00000;
            transform: translateY(-2px);
          }
        `}</style>

        <div className="auth-required">
          <h2 className="auth-title">Authentication Required</h2>
          <p className="auth-message">
            Please log in to view your book library.
          </p>
          <button onClick={onBack} className="back-button">
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  const filteredLibrary = getFilteredLibrary();

  return (
    <div className="library-container">
      {/* PDF Reader Modal */}
      {isReading && pdfUrl && selectedBook && (
        <div className="pdf-reader-modal">
          <div className="pdf-reader-header">
            <h3 className="pdf-title">{selectedBook.title}</h3>
            <div className="pdf-reader-controls">
              <div className="page-controls">
                <button 
                  className="page-btn" 
                  onClick={handlePrevPage}
                  disabled={currentPage <= 1}
                >
                  ←
                </button>
                <span className="page-info">
                  Page {currentPage} of {totalPages}
                </span>
                <button 
                  className="page-btn" 
                  onClick={handleNextPage}
                  disabled={currentPage >= totalPages}
                >
                  →
                </button>
              </div>
              <div className="reader-actions">
                <button 
                  className="action-btn download-btn"
                  onClick={() => handleDownloadBook(selectedBook.id, 'pdf')}
                >
                  ⬇️ Download
                </button>
                <button 
                  className="action-btn close-btn"
                  onClick={handleCloseReader}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
          <div className="pdf-viewer-container">
            {readMode === 'online' ? (
              <iframe 
                src={`${pdfUrl}#page=${currentPage}`}
                title="PDF Viewer"
                className="pdf-viewer"
                width="100%"
                height="100%"
                style={{ border: 'none' }}
              />
            ) : (
              <div className="download-message">
                <div className="download-icon">📚</div>
                <h4>Download Complete!</h4>
                <p>Your book has been downloaded successfully.</p>
                <button 
                  className="action-btn primary"
                  onClick={() => setReadMode('online')}
                >
                  Read Online
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Download Options Modal */}
      {showDownloadOptions && (
        <div className="modal-overlay">
          <div className="download-options-modal">
            <div className="modal-header">
              <h4>Download Options</h4>
              <button 
                className="close-btn"
                onClick={() => setShowDownloadOptions(null)}
              >
                ×
              </button>
            </div>
            <div className="modal-content">
              <p>Choose download format for:</p>
              <h5>{showDownloadOptions.title}</h5>
              
              <div className="download-options-list">
                <button 
                  className="download-option"
                  onClick={() => handleDownloadBook(showDownloadOptions.id, 'pdf')}
                >
                  <div className="option-icon">📄</div>
                  <div className="option-details">
                    <strong>PDF Format</strong>
                    <small>High quality, best for printing</small>
                  </div>
                  <div className="option-action">Download</div>
                </button>
                
                <button 
                  className="download-option"
                  onClick={() => handleDownloadBook(showDownloadOptions.id, 'epub')}
                >
                  <div className="option-icon">📱</div>
                  <div className="option-details">
                    <strong>EPUB Format</strong>
                    <small>Best for e-readers and mobile</small>
                  </div>
                  <div className="option-action">Download</div>
                </button>
                
                <button 
                  className="download-option"
                  onClick={() => handleDownloadBook(showDownloadOptions.id, 'sample')}
                >
                  <div className="option-icon">🔍</div>
                  <div className="option-details">
                    <strong>Sample PDF</strong>
                    <small>First few chapters only</small>
                  </div>
                  <div className="option-action">Download</div>
                </button>
              </div>
              
              <div className="modal-footer">
                <button 
                  className="btn secondary"
                  onClick={() => setShowDownloadOptions(null)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        /* Main Container */
        .library-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          padding: 20px;
          margin-top: 80px;
        }

        @media (max-width: 768px) {
          .library-container {
            margin-top: 70px;
            padding: 15px;
          }
        }

        /* Header */
        .library-header {
          max-width: 1400px;
          margin: 0 auto 2rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .library-title {
          font-family: 'Playfair Display', serif;
          font-size: 2.5rem;
          background: linear-gradient(135deg, #334155, #e63946);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin: 0;
          line-height: 1.2;
        }

        .book-count {
          background: #e63946;
          color: white;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.9rem;
          margin-left: 0.5rem;
        }

        .library-actions {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
        }

        /* Controls */
        .library-controls {
          max-width: 1400px;
          margin: 0 auto 2rem;
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .search-box {
          flex: 1;
          min-width: 250px;
        }

        .search-input {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          font-size: 1rem;
          transition: all 0.3s ease;
        }

        .search-input:focus {
          outline: none;
          border-color: #e63946;
          box-shadow: 0 0 0 3px rgba(230, 57, 70, 0.1);
        }

        .sort-dropdown {
          padding: 0.75rem 1rem;
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          font-size: 1rem;
          background: white;
          cursor: pointer;
          min-width: 150px;
        }

        .sort-dropdown:focus {
          outline: none;
          border-color: #e63946;
        }

        /* Buttons */
        .btn {
          padding: 0.75rem 1.25rem;
          border-radius: 10px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 0.95rem;
          border: none;
          white-space: nowrap;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .back-button {
          background: #f8fafc;
          color: #64748b;
          border: 1px solid #e2e8f0;
        }

        .back-button:hover {
          background: #f1f5f9;
          transform: translateY(-2px);
        }

        .refresh-button {
          background: #10b981;
          color: white;
        }

        .refresh-button:hover {
          background: #0d9668;
          transform: translateY(-2px);
        }

        .bulk-download-btn {
          background: #3b82f6;
          color: white;
        }

        .bulk-download-btn:hover {
          background: #2563eb;
          transform: translateY(-2px);
        }

        .action-btn {
          padding: 0.5rem 1rem;
          border-radius: 6px;
          border: none;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.3s ease;
        }

        .action-btn.primary {
          background: #e63946;
          color: white;
        }

        .action-btn.primary:hover {
          background: #d00000;
        }

        .action-btn.secondary {
          background: #6c757d;
          color: white;
        }

        .action-btn.secondary:hover {
          background: #545b62;
        }

        /* Library Stats */
        .library-stats {
          max-width: 1400px;
          margin: 0 auto 2rem;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
        }

        .stat-card {
          background: white;
          border-radius: 12px;
          padding: 1.5rem;
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
          text-align: center;
        }

        .stat-icon {
          font-size: 2rem;
          margin-bottom: 0.5rem;
        }

        .stat-number {
          font-size: 2rem;
          font-weight: 700;
          color: #334155;
          margin-bottom: 0.25rem;
        }

        .stat-label {
          color: #64748b;
          font-size: 0.9rem;
        }

        /* Library Grid */
        .library-grid {
          max-width: 1400px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 1.5rem;
        }

        @media (max-width: 768px) {
          .library-grid {
            grid-template-columns: 1fr;
          }
        }

        /* Book Card */
        .book-card {
          background: white;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0,0,0,0.08);
          transition: all 0.3s ease;
          border: 1px solid #f1f5f9;
          display: flex;
          flex-direction: column;
        }

        .book-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 25px rgba(0,0,0,0.15);
        }

        .book-image {
          height: 200px;
          overflow: hidden;
          position: relative;
        }

        .book-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }

        .book-card:hover .book-image img {
          transform: scale(1.05);
        }

        .book-badge {
          position: absolute;
          top: 10px;
          right: 10px;
          background: #ffd700;
          color: #333;
          padding: 0.3rem 0.8rem;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: bold;
          z-index: 1;
        }

        .book-content {
          padding: 1.5rem;
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .book-header {
          margin-bottom: 1rem;
        }

        .book-title {
          font-size: 1.2rem;
          font-weight: 600;
          margin: 0 0 0.5rem 0;
          color: #1a1a1a;
          line-height: 1.4;
        }

        .book-author {
          color: #6c757d;
          font-size: 0.95rem;
          margin-bottom: 0.5rem;
        }

        .book-category {
          display: inline-block;
          background: #e2e8f0;
          color: #475569;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-size: 0.8rem;
          margin-bottom: 1rem;
        }

        .book-meta {
          flex: 1;
          margin-bottom: 1.5rem;
        }

        .meta-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 0.75rem;
        }

        .meta-item {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .meta-label {
          font-size: 0.8rem;
          color: #64748b;
        }

        .meta-value {
          font-size: 0.9rem;
          color: #334155;
          font-weight: 500;
        }

        .book-actions {
          display: flex;
          gap: 0.5rem;
          margin-top: auto;
        }

        @media (max-width: 768px) {
          .book-actions {
            flex-direction: column;
          }
        }

        .book-action-btn {
          flex: 1;
          padding: 0.7rem;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 500;
          font-size: 0.9rem;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }

        .read-btn {
          background: #e63946;
          color: white;
        }

        .read-btn:hover {
          background: #d00000;
          transform: translateY(-2px);
        }

        .download-btn {
          background: #6c757d;
          color: white;
        }

        .download-btn:hover {
          background: #545b62;
          transform: translateY(-1px);
        }

        /* PDF Reader Modal */
        .pdf-reader-modal {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.95);
          z-index: 1000;
          display: flex;
          flex-direction: column;
        }

        .pdf-reader-header {
          background: rgba(255, 255, 255, 0.95);
          padding: 1rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid #e2e8f0;
        }

        .pdf-title {
          font-size: 1.2rem;
          font-weight: 600;
          color: #1a1a1a;
          margin: 0;
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .pdf-reader-controls {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .page-controls {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          background: #f8fafc;
          padding: 0.5rem 1rem;
          border-radius: 20px;
        }

        .page-btn {
          background: none;
          border: none;
          cursor: pointer;
          font-size: 1.2rem;
          color: #64748b;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          transition: all 0.2s ease;
        }

        .page-btn:hover {
          background: #e2e8f0;
          color: #334155;
        }

        .page-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .page-info {
          font-size: 0.9rem;
          color: #64748b;
          margin: 0 0.5rem;
        }

        .reader-actions {
          display: flex;
          gap: 0.5rem;
        }

        .close-btn {
          background: #dc3545;
          color: white;
          border: none;
          padding: 0.5rem 1rem;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.3s ease;
        }

        .close-btn:hover {
          background: #c82333;
          transform: translateY(-1px);
        }

        .pdf-viewer-container {
          flex: 1;
          overflow: auto;
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 2rem;
        }

        .pdf-viewer {
          max-width: 90%;
          max-height: 90%;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
        }

        .download-message {
          text-align: center;
          padding: 3rem;
          background: white;
          border-radius: 12px;
        }

        .download-icon {
          font-size: 3rem;
          margin-bottom: 1rem;
        }

        /* Download Options Modal */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1001;
          padding: 20px;
        }

        .download-options-modal {
          background: white;
          border-radius: 12px;
          max-width: 500px;
          width: 100%;
          max-height: 90vh;
          overflow: hidden;
        }

        .modal-header {
          padding: 1.5rem;
          border-bottom: 1px solid #e2e8f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .modal-header h4 {
          margin: 0;
          color: #334155;
        }

        .modal-content {
          padding: 1.5rem;
        }

        .download-options-list {
          margin: 1.5rem 0;
        }

        .download-option {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          margin-bottom: 0.75rem;
          background: white;
          cursor: pointer;
          transition: all 0.3s ease;
          width: 100%;
        }

        .download-option:hover {
          border-color: #e63946;
          background: #fef2f2;
          transform: translateY(-2px);
        }

        .option-icon {
          font-size: 1.5rem;
        }

        .option-details {
          flex: 1;
          text-align: left;
        }

        .option-details strong {
          display: block;
          color: #334155;
          margin-bottom: 0.25rem;
        }

        .option-details small {
          color: #64748b;
          font-size: 0.85rem;
        }

        .option-action {
          color: #e63946;
          font-weight: 600;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 1rem;
          margin-top: 1.5rem;
        }

        /* Empty Library */
        .empty-library {
          max-width: 600px;
          margin: 4rem auto;
          text-align: center;
          padding: 3rem 2rem;
          background: white;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.08);
        }

        .empty-icon {
          width: 80px;
          height: 80px;
          background: linear-gradient(135deg, #10b981, #059669);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
          font-size: 2rem;
          color: white;
        }

        .empty-title {
          font-family: 'Playfair Display', serif;
          color: #334155;
          margin-bottom: 1rem;
          font-size: 1.8rem;
        }

        .empty-message {
          color: #64748b;
          margin-bottom: 2rem;
          font-size: 1rem;
          line-height: 1.6;
          max-width: 400px;
          margin-left: auto;
          margin-right: auto;
        }

        /* Loading */
        .loading-spinner {
          text-align: center;
          padding: 4rem;
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
          max-width: 600px;
          margin: 2rem auto;
        }

        .spinner {
          width: 50px;
          height: 50px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #e63946;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 1rem;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .loading-text {
          color: #64748b;
          font-size: 1rem;
        }
      `}</style>

      <div className="library-header">
        <h1 className="library-title">
          My Library
          {library.length > 0 && <span className="book-count">{library.length}</span>}
        </h1>
        
        <div className="library-actions">
          <button onClick={onBack} className="btn back-button">
            ← Back to Store
          </button>
          <button onClick={loadLibrary} className="btn refresh-button">
            🔄 Refresh
          </button>
          {library.length > 0 && (
            <button onClick={handleBulkDownload} className="btn bulk-download-btn">
              📦 Download All
            </button>
          )}
        </div>
      </div>

      {library.length > 0 && (
        <>
          <div className="library-stats">
            <div className="stat-card">
              <div className="stat-icon">📚</div>
              <div className="stat-number">{library.length}</div>
              <div className="stat-label">Total Books</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">⬇️</div>
              <div className="stat-number">
                {library.reduce((sum, book) => sum + (book.download_count || 0), 0)}
              </div>
              <div className="stat-label">Total Downloads</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">📖</div>
              <div className="stat-number">
                {library.filter(b => b.last_read).length}
              </div>
              <div className="stat-label">Books Read</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">📄</div>
              <div className="stat-number">
                {library.filter(b => b.pdf_file).length}
              </div>
              <div className="stat-label">PDF Available</div>
            </div>
          </div>

          <div className="library-controls">
            <div className="search-box">
              <input
                type="text"
                className="search-input"
                placeholder="Search books by title, author..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <select 
              className="sort-dropdown"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="recent">Most Recent</option>
              <option value="title">Title A-Z</option>
              <option value="author">Author A-Z</option>
              <option value="downloads">Most Downloaded</option>
            </select>
          </div>
        </>
      )}

      {loading ? (
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p className="loading-text">Loading your library...</p>
        </div>
      ) : filteredLibrary.length === 0 ? (
        <div className="empty-library">
          <div className="empty-icon">📚</div>
          <h3 className="empty-title">Your Library is Empty</h3>
          <p className="empty-message">
            {searchQuery ? 
              `No books found for "${searchQuery}". Try a different search.` :
              "You haven't purchased any books yet. Browse our collection and purchase books to add them to your library."
            }
          </p>
          {!searchQuery && (
            <button onClick={onBack} className="action-btn primary">
              Browse Books
            </button>
          )}
          {searchQuery && (
            <button 
              onClick={() => setSearchQuery('')}
              className="action-btn secondary"
            >
              Clear Search
            </button>
          )}
        </div>
      ) : (
        <div className="library-grid">
          {filteredLibrary.map((book) => (
            <div key={book.id} className="book-card">
              <div className="book-image">
                <img 
                  src={book.thumbnail_url || 'https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=400&h=600&fit=crop'} 
                  alt={book.title}
                />
                {book.last_read && (
                  <div className="book-badge" title="Recently Read">
                    📖
                  </div>
                )}
              </div>
              
              <div className="book-content">
                <div className="book-header">
                  <h3 className="book-title">{book.title}</h3>
                  <p className="book-author">by {book.author}</p>
                  {book.category_name && (
                    <span className="book-category">{book.category_name}</span>
                  )}
                </div>
                
                <div className="book-meta">
                  <div className="meta-grid">
                    <div className="meta-item">
                      <span className="meta-label">Pages:</span>
                      <span className="meta-value">{book.pdf_pages || 'N/A'}</span>
                    </div>
                    <div className="meta-item">
                      <span className="meta-label">Size:</span>
                      <span className="meta-value">{formatFileSize(book.pdf_size)}</span>
                    </div>
                    <div className="meta-item">
                      <span className="meta-label">Last Read:</span>
                      <span className="meta-value">{formatDate(book.last_read)}</span>
                    </div>
                    <div className="meta-item">
                      <span className="meta-label">Downloads:</span>
                      <span className="meta-value">{book.download_count || 0}</span>
                    </div>
                  </div>
                </div>
                
                <div className="book-actions">
                  <button 
                    className="book-action-btn read-btn"
                    onClick={() => handleReadBook(book.id)}
                    title="Read Online"
                  >
                    📖 Read Online
                  </button>
                  
                  <button 
                    className="book-action-btn download-btn"
                    onClick={() => setShowDownloadOptions({ id: book.id, title: book.title })}
                    title="Download Options"
                  >
                    ⬇️ Download
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BookLibrary;